import { Injectable, inject } from '@angular/core';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { buildGuidebookUrl } from 'src/shared/util/guidebook-url';
import { Communication } from 'src/providers/communication/communication';
import { MessagesHandoffService } from 'src/shared/services/messages-handoff.service';
import { ReservationPage } from 'src/pages/reservation/reservation';
import { GuidebookPreviewComponent } from 'src/pages/marketing/components/guidebook-preview/guidebook-preview.component';
import { Reservation, ReservationGuest } from 'src/models/schedule-weekly-view.model';
import type { CustomerGuest } from 'src/models/customer-guest.model';

export interface ReservationNavContext {
  address?: string;
  addressId?: number | string;
}

@Injectable({ providedIn: 'root' })
export class ReservationInteractionService {
  private readonly windowService = inject(WindowService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly communication = inject(Communication);
  private readonly messagesHandoff = inject(MessagesHandoffService);

  openDetail(reservation: Reservation, context: ReservationNavContext = {}): void {
    const guestReservation = reservation?.object ?? reservation;
    // Inquiry cards reuse this modal; the parser tags them state === 'inquiry'
    // (raw payload type === 'inquiry'). Carry the flag so the page fetches the
    // inquiry endpoint and shows the "Inquiry" title.
    const isInquiry =
      reservation?.state === 'inquiry' || (guestReservation as any)?.type === 'inquiry';
    const params = {
      guestReservation,
      isInquiry,
      address: context.address,
      addressId: context.addressId,
    };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle(isInquiry ? 'Inquiry' : 'Reservation');
      this.storage.save('dialog-params', params);
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(ReservationPage);
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  openGuidebook(reservation: Reservation, context: ReservationNavContext = {}): void {
    // Callers pass either the parsed schedule `Reservation` (raw payload under
    // `.object`) or the raw guest reservation from the detail page (fields at
    // the top level), so resolve from both shapes.
    const raw = (reservation?.object ?? reservation) as any;
    const guidebookUrl = reservation?.guidebookUrl ?? raw?.guidebook_url;
    if (guidebookUrl) {
      this.util.openUrl(guidebookUrl);
      return;
    }
    // Legacy fallback: open the public guidebook by reservation uuid. This is
    // current production behavior for reservations that have not yet received
    // a backend-provided `guidebookUrl`.
    const uuid = raw?.uuid ?? (reservation as any)?.uuid;
    if (uuid) {
      this.util.openUrl(buildGuidebookUrl(uuid));
      return;
    }
    const params = {
      guidebookConfigId: null,
      reservationId: reservation?.id,
      addressId: context.addressId,
    };
    this.storage.save('dialog-params', params);
    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.navigateTo(
        'guidebook-preview',
        params,
        GuidebookPreviewComponent,
      );
    } else {
      this.navCtrl.navigateForward('guidebook-preview', params);
    }
  }

  async openMessageThread(reservation: Reservation): Promise<void> {
    const raw = (reservation?.object ?? reservation) as any;
    const primaryGuest = this.primaryGuest(reservation);
    const phone = primaryGuest?.phone || reservation?.phone || raw?.guest_phone || raw?.phone;
    const chatRoomKeyFromGuest = this.guestChatRoomKey(primaryGuest);
    const guestDisplayName = this.guestDisplayName(reservation, primaryGuest);

    if (chatRoomKeyFromGuest) {
      await this.openExistingGuestThread(chatRoomKeyFromGuest, guestDisplayName);
      return;
    }

    if (!phone && !primaryGuest?.id) {
      this.util.showError('No guest phone number on file');
      return;
    }
    const normalize = (p?: string): string => (p || '').replace(/\D+/g, '');
    const target = normalize(phone);
    let chatRoomKey: string | undefined;
    let chatRoomData: any;
    if (target) {
      try {
        const proxyNumbers = await this.communication.getProxyNumbers();
        for (const proxy of proxyNumbers || []) {
          const room = (proxy?.chat_rooms || []).find(
            (r: any) => normalize(r?.data?.phone_number) === target,
          );
          if (room?.key) {
            chatRoomKey = room.key;
            chatRoomData = room;
            break;
          }
        }
      } catch (_err) {
        // Network / API error — fall through to the messages list so the host
        // can still find the conversation manually.
      }
    }
    if (chatRoomKey) {
      await this.openExistingGuestThread(chatRoomKey, guestDisplayName, chatRoomData);
      return;
    }

    if (!primaryGuest?.id) {
      this.util.showError('No conversation found with this guest yet');
      return;
    }

    if (!phone) {
      this.util.showError('Add a phone number to message this guest.');
      return;
    }

    await this.openNewGuestThread(primaryGuest, guestDisplayName, phone);
  }

  private async openExistingGuestThread(chatRoomKey: string, pageTitle: string, chatRoomData?: any): Promise<void> {
    if (this.windowService.isDesktopRes) {
      this.messagesHandoff.set({ type: 'room', chatRoomKey, details: { pageTitle, chatRoom: chatRoomData ?? null } });
      await this.rightSidePanelService.closeRightSidePanel();
      this.navCtrl.navigateForward('messages', {});
    } else {
      await this.storage.delete('proId');
      this.navCtrl.navigateForward('message-thread', {
        backPage: 'schedule',
        chatRoomKey,
        pageTitle,
        chatRoom: chatRoomData,
      });
    }
  }

  private async openNewGuestThread(guest: ReservationGuest, pageTitle: string, guestPhone?: string): Promise<void> {
    const params = {
      backPage: 'schedule',
      guestId: guest.id,
      pageTitle,
      guestPhone,
    };
    if (this.windowService.isDesktopRes) {
      this.messagesHandoff.set({ type: 'guest', guest: this.toCustomerGuest(guest, pageTitle, guestPhone) });
      await this.rightSidePanelService.closeRightSidePanel();
      this.navCtrl.navigateForward('messages', {});
    } else {
      await this.storage.delete('proId');
      await this.storage.delete('chatRoomKey');
      this.navCtrl.navigateForward('message-thread', params);
    }
  }

  private primaryGuest(reservation: Reservation): ReservationGuest | undefined {
    const raw = (reservation?.object ?? reservation) as any;
    const guests = (reservation?.guests || raw?.guests || []).map((guest: any) => ({
      ...guest,
      isBooker: guest.isBooker ?? guest.is_booker,
      isStaying: guest.isStaying ?? guest.is_staying,
      chatRoomKey: guest.chatRoomKey || guest.chat_room_key || null,
    }));
    return guests.find((guest) => guest.isBooker)
      ?? guests.find((guest) => guest.isStaying)
      ?? guests[0];
  }

  private guestChatRoomKey(guest?: ReservationGuest): string | undefined {
    return guest?.chatRoomKey || (guest as any)?.chat_room_key || undefined;
  }

  private guestDisplayName(reservation: Reservation, guest?: ReservationGuest): string {
    return guest?.name
      || [reservation?.guestFirstName, reservation?.guestLastName].filter(Boolean).join(' ').trim()
      || reservation?.guestName
      || 'Guest';
  }

  private toCustomerGuest(guest: ReservationGuest, name: string, phone?: string): CustomerGuest {
    return {
      id: guest.id,
      name,
      email: guest.email || null,
      phone: guest.phone || phone || null,
      platform: null,
      created_at: '',
      chat_room_key: this.guestChatRoomKey(guest) || null,
    };
  }
}
